import { useCallback, useMemo, useState } from 'react';
import type { ShippingForRouting } from '@/pages/Business/pages/Shippings/models/routing.types';
import type { RouteMonitoringVisit } from '../models';
import {
  UNKNOWN_STATUS_LABEL,
  buildRouteShippingFacets,
  countFacetOptions,
  getVisitShippingNumbers,
  type RouteShippingFacet,
} from '../utils/route-visit-shipping-filters';

export interface RouteShippingLabels {
  statusLabel?: string;
  serviceLabel: string;
}

const getStatusValue = (facet: RouteShippingFacet): string =>
  facet.statusLabel ?? '';

const getServiceValue = (facet: RouteShippingFacet): string =>
  facet.serviceLabel;

const getStatusOptionLabel = (statusLabel: string): string =>
  statusLabel || UNKNOWN_STATUS_LABEL;

/**
 * Filtros por estado del pedido y por servicio dentro de una ruta. Cuentan
 * pedidos únicos (no visitas) y cada fila cuenta sobre lo que deja la otra,
 * así "En ruta" con "Flex" elegido dice cuántos Flex están en ruta.
 */
const useRouteVisitShippingFilters = (
  visits: RouteMonitoringVisit[] | undefined,
  enrichedByNumber: Map<number, ShippingForRouting>,
  pendingStatusCount: number
) => {
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const facets = useMemo(
    () => buildRouteShippingFacets(visits ?? [], enrichedByNumber),
    [visits, enrichedByNumber]
  );

  const facetsForStatusRow = useMemo(
    () =>
      selectedService === null
        ? facets
        : facets.filter((facet) => getServiceValue(facet) === selectedService),
    [facets, selectedService]
  );

  const facetsForServiceRow = useMemo(
    () =>
      selectedStatus === null
        ? facets
        : facets.filter((facet) => getStatusValue(facet) === selectedStatus),
    [facets, selectedStatus]
  );

  const statusOptions = useMemo(
    () =>
      countFacetOptions(
        facetsForStatusRow,
        getStatusValue,
        getStatusOptionLabel,
        selectedStatus
      ),
    [facetsForStatusRow, selectedStatus]
  );

  const serviceOptions = useMemo(
    () =>
      countFacetOptions(
        facetsForServiceRow,
        getServiceValue,
        (serviceLabel) => serviceLabel,
        selectedService
      ),
    [facetsForServiceRow, selectedService]
  );

  const isActive = selectedStatus !== null || selectedService !== null;

  const matchingNumbers = useMemo(
    () =>
      new Set(
        facetsForStatusRow
          .filter(
            (facet) =>
              selectedStatus === null || getStatusValue(facet) === selectedStatus
          )
          .map((facet) => facet.number)
      ),
    [facetsForStatusRow, selectedStatus]
  );

  const facetByNumber = useMemo(
    () => new Map(facets.map((facet) => [facet.number, facet])),
    [facets]
  );

  const matchesShipping = useCallback(
    (shippingNumber: number) => !isActive || matchingNumbers.has(shippingNumber),
    [isActive, matchingNumbers]
  );

  const filterVisits = useCallback(
    (visitsToFilter: RouteMonitoringVisit[]) =>
      isActive
        ? visitsToFilter.filter((visit) =>
            getVisitShippingNumbers(visit).some((shippingNumber) =>
              matchingNumbers.has(shippingNumber)
            )
          )
        : visitsToFilter,
    [isActive, matchingNumbers]
  );

  const getShippingLabels = useCallback(
    (shippingNumber: number): RouteShippingLabels | null => {
      const facet = facetByNumber.get(shippingNumber);
      if (!facet) return null;
      return {
        statusLabel: facet.statusLabel ?? undefined,
        serviceLabel: facet.serviceLabel,
      };
    },
    [facetByNumber]
  );

  return {
    selectedStatus,
    setSelectedStatus,
    selectedService,
    setSelectedService,
    statusOptions,
    serviceOptions,
    statusRowTotal: facetsForStatusRow.length,
    serviceRowTotal: facetsForServiceRow.length,
    totalCount: facets.length,
    matchingCount: matchingNumbers.size,
    isActive,
    isLoadingStatuses: pendingStatusCount > 0,
    matchesShipping,
    filterVisits,
    getShippingLabels,
  };
};

export type RouteVisitShippingFilters = ReturnType<
  typeof useRouteVisitShippingFilters
>;

export default useRouteVisitShippingFilters;
